// Static tables of the reference tool (unione-viti-legno-lavoro.html) that are engine logic, not catalog data.

import type { Translation } from "../../../hooks/useTranslations";

export type MaterialTypeTexts = Translation["screwConnectorModule"]["calculation"]["materialTypes"];

// Material category names of the source DB (English) -> historical Italian keys of the "Tipo di materiale" menu.
export const TIMBER_TIPO_MAP: Record<string, string> = {
  "Solidwood Softwood": "Legno massiccio conifera",
  "Glulam Softwood": "Legno lamellare incollato",
  "CLT Softwood": "Pannelli CLT",
  "LVL Softwood": "LVL (Laminated Veneer Lumber)",
  "Solidwood Hardwood": "Legno massiccio latifoglia",
  "LVL Hardwood": "LVL (Laminated Veneer Lumber)",
  Panels: "Pannelli a base di legno",
  Steel: "Acciaio",
};
// Presentational sub-category when the DB row has no species (Panels/Steel/USER flat lists).
export const TIMBER_SUBCAT_FALLBACK: Record<string, string> = {
  "Pannelli a base di legno": "Pannelli OSB / cemento-legno",
  Acciaio: "Acciaio da carpenteria",
};
// Inverse of TIMBER_TIPO_MAP: menu key -> substrate key of the screw DB (estrazioneAssiale/estrazioneTesta).
// LVL Softwood/Hardwood share the same menu key: ambiguous, Softwood is assumed (as in the source).
export const TIMBER_TIPO_MAP_REVERSE: Record<string, string> = {
  "Legno massiccio conifera": "Solidwood Softwood",
  "Legno lamellare incollato": "Glulam Softwood",
  "Pannelli CLT": "CLT Softwood",
  "LVL (Laminated Veneer Lumber)": "LVL Softwood",
  "Legno massiccio latifoglia": "Solidwood Hardwood",
  "Pannelli a base di legno": "Panels",
  Acciaio: "Steel",
};
// The keys above stay the source's Italian names: shown through their translation.
const MATERIAL_KEY_TEXT: Record<string, keyof MaterialTypeTexts> = {
  "Legno massiccio conifera": "solidwoodSoftwood",
  "Legno lamellare incollato": "glulam",
  "Pannelli CLT": "clt",
  "LVL (Laminated Veneer Lumber)": "lvl",
  "Legno massiccio latifoglia": "solidwoodHardwood",
  "Pannelli a base di legno": "panels",
  Acciaio: "steel",
  "Pannelli OSB / cemento-legno": "panelsSpecies",
  "Acciaio da carpenteria": "steelSpecies",
};
// Label of a "Tipo di materiale" / "Specie legnosa" key: the translated name of the engine's keys, the value itself
// otherwise (species and classes come from the catalog, already in the user's language).
export const materialLabel = (t: MaterialTypeTexts, key: string): string => {
  const textKey = MATERIAL_KEY_TEXT[key];
  return textKey ? t[textKey] : key;
};

// Backend substrate category -> substrate name of the source DB.
export const SUBSTRATE_NAME: Record<string, string> = {
  solidwood_softwood: "Solidwood Softwood",
  glulam_softwood: "Glulam Softwood",
  clt_softwood: "CLT Softwood",
  lvl_softwood: "LVL Softwood",
  solidwood_hardwood: "Solidwood Hardwood",
  lvl_hardwood: "LVL Hardwood",
  panels: "Panels",
  steel: "Steel",
};
// Backend family connection-type categories -> keys of the source DB `moduliCompatibili`.
export const MODULE_NAME: Record<string, string> = {
  shear_screws: "Viti a taglio",
  steel_to_timber: "Acciaio-Legno",
  timber_to_timber: "Legno-Legno",
};

// Backend load-duration codes <-> #durataCarico values (the engine works with the latter, e.g. panelKmod).
export const LOAD_DURATION_TO_HTML: Record<string, string> = {
  permanent: "permanente",
  long_term: "lungaDurata",
  medium_term: "medioTermine",
  short_term: "breveTermine",
  instantaneous: "istantanea",
};
export const LOAD_DURATION_FROM_HTML: Record<string, string> = Object.fromEntries(
  Object.entries(LOAD_DURATION_TO_HTML).map(([k, v]) => [v, k]),
);

// Washers temporarily not usable (user request 2026-10-08: "al momento non usare la rondella HUS815"): excluded from
// menu, solution search and calculation. Remove from this set to re-enable.
export const WASHERS_DISABLED = new Set(["HUS815"]);

// kmod (EC5 Tab. 3.1) and gammaM tables of the source, used as fallback when the backend tables are missing a value.
export type KmodTable = Record<number, Record<string, number>>;
export const KMOD_DEFAULT: KmodTable = {
  1: { permanente: 0.6, lungaDurata: 0.7, medioTermine: 0.8, breveTermine: 0.9, istantanea: 1.1 },
  2: { permanente: 0.6, lungaDurata: 0.7, medioTermine: 0.8, breveTermine: 0.9, istantanea: 1.1 },
  3: { permanente: 0.5, lungaDurata: 0.55, medioTermine: 0.65, breveTermine: 0.7, istantanea: 0.9 },
};
export type GammaMEc5Table = Record<string, { label: string; val: number }>;
export type GammaMNtcTable = Record<string, { label: string; A: number; B: number }>;
export const GAMMA_M_EC5_DEFAULT: GammaMEc5Table = {
  legnoMassiccio: { label: "Legno massiccio", val: 1.3 },
  legnoLamellareIncollato: { label: "Legno lamellare incollato", val: 1.25 },
  lvlCompensatoOSB: { label: "LVL, compensato, OSB", val: 1.2 },
  pannelliParticelle: { label: "Pannelli di particelle", val: 1.3 },
  pannelliFibraAltaDensita: { label: "Pannelli di fibre, alta densità", val: 1.3 },
  pannelliFibraMediaDensita: { label: "Pannelli di fibre, media densità", val: 1.3 },
  pannelliFibraMDF: { label: "Pannelli di fibre, MDF", val: 1.3 },
  pannelliFibraBassaDensita: { label: "Pannelli di fibre, bassa densità", val: 1.3 },
  connessioni: { label: "Connessioni", val: 1.3 },
  mezziUnionePiastraPunzonata: { label: "Mezzi di unione a piastra metallica punzonata", val: 1.25 },
  combinazioniAccidentali: { label: "Combinazioni accidentali", val: 1.0 },
};
export const GAMMA_M_NTC2018_DEFAULT: GammaMNtcTable = {
  legnoMassiccio: { label: "Legno massiccio", A: 1.5, B: 1.45 },
  legnoLamellare: { label: "Legno lamellare incollato", A: 1.45, B: 1.35 },
  pannelliCLT: { label: "Pannelli di tavole incollate a strati incrociati (CLT)", A: 1.45, B: 1.35 },
  pannelliParticelleFibre: { label: "Pannelli di particelle o di fibre", A: 1.4, B: 1.3 },
  lvlCompensatoOSB: { label: "LVL, compensato, OSB", A: 1.4, B: 1.3 },
  unioni: { label: "Unioni", A: 1.5, B: 1.4 },
  eccezionali: { label: "Combinazioni eccezionali", A: 1.0, B: 1.0 },
};
// The tool always evaluates a CONNECTION: "Connessioni" (EC5) / "Unioni" (NTC2018), column A/B selectable.
export const GAMMA_M_CATEGORIA_FISSA: Record<string, string> = { EC5: "connessioni", NTC2018: "unioni" };

// Display order of the source DB (TC_SCREWS_DB families, TC_TIMBER_DB classes): the backend lists are sorted
// alphabetically, so the catalog adapter re-sorts by these lists (after the admin `order`) to keep the menus,
// the default selections (first option) and the solution search order identical to the reference tool.
export const SCREW_FAMILY_ORDER = [
  "HBS",
  "HBS EVO",
  "HBS HARDWOOD",
  "HBS EVO C5",
  "HBS PLATE",
  "HBS PLATE EVO",
  "HBS PLATE A4",
  "VGS",
  "VGS PLATE",
  "VGS EVO",
  "VGS EVO C5",
  "VGS A4",
  "VGZ",
  "VGZ EVO",
  "VGZ HARDWOOD",
  "VGZ EVO C5",
  "TBS",
  "TBS EVO",
  "TBS EVO C5",
  "TBS MAX",
  "TBS FRAME",
  "LBS",
  "LBS EVO",
  "LBS HARDWOOD",
  "LBS HARDWOOD EVO",
];
export const TIMBER_CATEGORY_ORDER = [
  "solidwood_softwood",
  "glulam_softwood",
  "clt_softwood",
  "lvl_softwood",
  "solidwood_hardwood",
  "lvl_hardwood",
  "panels",
  "steel",
];
export const TIMBER_CLASS_ORDER = [
  "C14", "C16", "C18", "C20", "C22", "C24", "C27", "C30", "C35", "C40", "C45", "C50",
  "GL20h", "GL22h", "GL24h", "GL26h", "GL28h", "GL30h", "GL32h",
  "GL20c", "GL22c", "GL24c", "GL26c", "GL28c", "GL30c", "GL32c",
  "CLT",
  "LVL 32 P", "LVL 48 P", "LVL (…)",
  "D30 oak", "D30 beech", "D30 birch",
  "Beech LVL", "FST - GL75h",
  "OSB-2", "Magnumboard ®", "Compensato EN636-1", "Compensato EN636-2", "Compensato EN636-3",
  "Pannello OSB/3", "Pannello OSB/4",
  "Pannello particelle P4", "Pannello particelle P5", "Pannello particelle P6", "Pannello particelle P7",
  "S235", "S275", "S355",
];
